<!--
  PricingModeBadge -- says how a product is priced: "Fixed price" or
  "Store pricing" (Jake, 2026-09-29: "very clear when a product has been set
  to per-product pricing vs using the store-wide margin tables").

  Mounted everywhere a product appears in the editor: the product card, each
  products-table row, the editor header, and the Pricing panel. The two modes
  get distinct colors AND distinct icons so the difference survives a
  glance, a screenshot, and color blindness.

  `data-pricing-mode` carries the raw mode for ui-runner `attribute_matches`,
  so a scenario can bind the mode without depending on the copy. An unknown
  mode renders nothing rather than a guessed label.
-->
<template>
  <span
    v-if="label"
    :data-testid="testid"
    :data-pricing-mode="normalized"
    :class="['pricing-mode-badge', `pricing-mode-badge--${normalized}`]"
    :title="title"
    >{{ label }}</span
  >
</template>

<script setup>
import { computed } from "vue";
import {
  PRICING_MODE_FIXED,
  normalizePricingMode,
  pricingModeLabel,
} from "./pricingMode";

const props = defineProps({
  mode: { type: String, default: null },
  testid: { type: String, default: "pricing-mode-badge" },
});

const normalized = computed(() => normalizePricingMode(props.mode));
const label = computed(() => pricingModeLabel(normalized.value));
const title = computed(() =>
  normalized.value === PRICING_MODE_FIXED
    ? "This product has its own price for each size"
    : "This product is priced by the store's pricing tables",
);
</script>

<style scoped>
.pricing-mode-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  border: 1px solid transparent;
}

/* The icon is generated content (a Material Icons ligature), not a text
   node, so the badge's textContent is exactly the label -- which is what
   ui-runner `text_matches` reads. The empty alt text (`/ ""`) keeps
   screen readers from announcing the ligature name. */
.pricing-mode-badge::before {
  font-family: "Material Icons";
  font-size: 14px;
  font-weight: normal;
  line-height: 1;
}

.pricing-mode-badge--fixed::before {
  content: "push_pin" / "";
}

.pricing-mode-badge--store::before {
  content: "table_chart" / "";
}

/* Fixed price: amber -- a per-product override the store tables do not touch. */
.pricing-mode-badge--fixed {
  background: #fff4e0;
  color: #8a4b00;
  border-color: #f0c27a;
}

/* Store pricing: blue -- follows the store-wide tables. */
.pricing-mode-badge--store {
  background: #e8f1fb;
  color: #1d4f8a;
  border-color: #a9c7ea;
}
</style>
